"use client";

import React, { useState } from "react";
import Link from "next/link";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";

export default function CheckoutPage() {
  const { items, removeFromCart, updateQuantity, clearCart, totalPrice, proceedToShopifyCheckout, checkoutLoading, checkoutError } = useCart();
  const [orderPlaced, setOrderPlaced] = useState(false);

  const shipping = items?.length > 0 ? 8.99 : 0;
  const subtotal = totalPrice;
  const grandTotal = subtotal + shipping;

  const handlePlaceOrder = async () => {
    await proceedToShopifyCheckout();
  };

  return (
    <main className="bg-pmu-cream min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto px-6 pt-28 pb-16">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-pmu-taupe mb-8 uppercase tracking-widest" style={{ letterSpacing: "0.1em" }}>
          <Link href="/homepage" className="hover:text-pmu-espresso transition-colors">Home</Link>
          <span>/</span>
          <Link href="/purchase" className="hover:text-pmu-espresso transition-colors">Shop</Link>
          <span>/</span>
          <span className="text-pmu-espresso font-700">Checkout</span>
        </nav>

        {orderPlaced ? (
          /* Order Confirmation */
          (<div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-xl bg-pmu-amber-pale flex items-center justify-center mb-6 border border-pmu-amber/30">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-pmu-espresso">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h1 className="font-fraunces text-4xl text-pmu-espresso mb-3">Order Placed!</h1>
            <p className="text-pmu-taupe text-base mb-8 max-w-md">
              Thank you for your order. We&apos;ll be in touch soon with your shipping details.
            </p>
            <Link href="/purchase" className="btn-primary text-xs">
              Continue Shopping
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>)
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Order Items */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <h1 className="font-fraunces text-3xl text-pmu-espresso mb-2">Your Cart</h1>

              {items?.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-card border border-pmu-taupe-light/20 p-12 flex flex-col items-center text-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-pmu-amber-pale flex items-center justify-center border border-pmu-amber/20">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-pmu-espresso">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 01-8 0"/>
                    </svg>
                  </div>
                  <p className="text-pmu-espresso font-700 text-base uppercase tracking-wide" style={{ letterSpacing: "0.08em" }}>Your cart is empty</p>
                  <p className="text-pmu-taupe text-sm">Add some cookies to get started!</p>
                  <Link href="/purchase" className="btn-primary text-xs mt-2">
                    Shop Now
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </Link>
                </div>
              ) : (
                items?.map((item) => (
                  <div key={item?.id} className="bg-white rounded-2xl shadow-card border border-pmu-taupe-light/20 p-6 flex items-center gap-5">
                    {/* Cookie icon */}
                    <div className="w-14 h-14 rounded-xl bg-pmu-amber-pale flex items-center justify-center flex-shrink-0 border border-pmu-amber/20">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-pmu-espresso">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="8" cy="10" r="1.5" fill="currentColor" stroke="none"/>
                        <circle cx="14" cy="8" r="1" fill="currentColor" stroke="none"/>
                        <circle cx="15" cy="14" r="1.5" fill="currentColor" stroke="none"/>
                        <circle cx="9" cy="15" r="1" fill="currentColor" stroke="none"/>
                      </svg>
                    </div>

                    {/* Item info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-700 text-pmu-espresso text-sm uppercase tracking-wide" style={{ letterSpacing: "0.06em" }}>
                        Pick Me Up — {item?.label}
                      </p>
                      <p className="text-pmu-taupe text-xs mt-0.5">
                        CAD ${item?.price?.toFixed(2)} each
                      </p>
                    </div>

                    {/* Quantity controls */}
                    <div className="flex items-center bg-pmu-cream border border-pmu-taupe-light/25 rounded-lg overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item?.id, item?.quantity - 1)}
                        className="w-9 h-9 flex items-center justify-center text-pmu-espresso hover:bg-pmu-amber-pale transition-colors font-700"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="w-9 text-center font-700 text-pmu-espresso text-sm">
                        {item?.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item?.id, item?.quantity + 1)}
                        className="w-9 h-9 flex items-center justify-center text-pmu-espresso hover:bg-pmu-amber-pale transition-colors font-700"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    {/* Line total */}
                    <div className="text-right flex-shrink-0 w-24">
                      <p className="font-700 text-pmu-espresso text-sm">
                        CAD ${(item?.price * item?.quantity)?.toFixed(2)}
                      </p>
                    </div>

                    {/* Remove */}
                    <button
                      onClick={() => removeFromCart(item?.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-pmu-taupe hover:text-red-500 transition-colors flex-shrink-0"
                      aria-label="Remove item"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-card border border-pmu-taupe-light/20 p-7 sticky top-28">
                <h2 className="font-fraunces text-2xl text-pmu-espresso mb-6">Order Summary</h2>

                {/* Line items summary */}
                <div className="flex flex-col gap-3 mb-5">
                  {items?.map((item) => (
                    <div key={item?.id} className="flex items-center justify-between text-sm">
                      <span className="text-pmu-taupe text-xs">
                        {item?.label} × {item?.quantity}
                      </span>
                      <span className="font-700 text-pmu-espresso text-xs">
                        CAD ${(item?.price * item?.quantity)?.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {items?.length > 0 && (
                  <div className="border-t border-pmu-taupe-light/20 pt-4 flex flex-col gap-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-pmu-taupe text-xs">Subtotal</span>
                      <span className="font-700 text-pmu-espresso text-xs">CAD ${subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-pmu-taupe text-xs">Shipping</span>
                      <span className="font-700 text-pmu-espresso text-xs">CAD ${shipping?.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-pmu-taupe-light/20 pt-3 flex items-center justify-between">
                      <span className="font-700 text-pmu-espresso text-sm uppercase tracking-wide" style={{ letterSpacing: "0.08em" }}>Total</span>
                      <span className="font-900 text-pmu-espresso text-lg">CAD ${grandTotal?.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {items?.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-pmu-taupe text-xs">No items in cart</p>
                  </div>
                ) : (
                  <>
                    {checkoutError && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-red-600 text-xs">{checkoutError}</p>
                      </div>
                    )}
                    <button
                      onClick={handlePlaceOrder}
                      disabled={checkoutLoading}
                      className={`btn-primary w-full justify-center text-xs ${checkoutLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      {checkoutLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Redirecting to Shopify…
                        </>
                      ) : (
                        <>
                          Checkout with Shopify
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </>
                      )}
                    </button>
                  </>
                )}

                {/* Trust signals */}
                <div className="flex flex-col gap-2 mt-5 pt-5 border-t border-pmu-taupe-light/15">
                  <span className="flex items-center gap-2 text-xs text-pmu-taupe uppercase tracking-wide" style={{ letterSpacing: "0.08em" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Secure Checkout
                  </span>
                  <span className="flex items-center gap-2 text-xs text-pmu-taupe uppercase tracking-wide" style={{ letterSpacing: "0.08em" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2" />
                      <path d="M16 8h4l3 5v3h-7V8z" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    Fast Shipping
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
